import React, { useState, useEffect, useCallback } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { Plus, Trash2, Upload, Save, Clock, Award, Package } from 'lucide-react';

const SessionForm = ({ games, users, onSave, onCancel, initialData }) => {
  const isEditMode = !!initialData?.id;
  
  const { register, control, handleSubmit, reset, watch, formState: { errors } } = useForm({
    defaultValues: {
      date: new Date().toISOString().split('T')[0],
      gameId: '',
      hours: 0,
      minutes: 0,
      results: [{ playerId: '', rank: 1, score: '', leaguePoints: null }]
    }
  });
  
  const { fields, append, remove } = useFieldArray({
    control,
    name: "results"
  });

  const [preview, setPreview] = useState(null);
  const [file, setFile] = useState(null);
  
  // Expansions state
  const [availableExpansions, setAvailableExpansions] = useState([]);
  const [selectedExpansionIds, setSelectedExpansionIds] = useState([]);
  const [loadingExpansions, setLoadingExpansions] = useState(false);
  
  const watchedGameId = watch('gameId');

  // Load expansions when game changes
  const loadExpansions = useCallback(async (gameId) => {
    if (!gameId) {
      setAvailableExpansions([]);
      return;
    }
    
    setLoadingExpansions(true);
    try {
      const res = await fetch(`/api/games/${gameId}/expansions`);
      if (res.ok) {
        const data = await res.json();
        setAvailableExpansions(data);
      }
    } catch (err) {
      console.error('Failed to load expansions:', err);
    } finally {
      setLoadingExpansions(false);
    }
  }, []);

  // Load expansions when game selection changes
  useEffect(() => {
    if (watchedGameId) {
      loadExpansions(watchedGameId);
    }
  }, [watchedGameId, loadExpansions]);

  // Pre-fill form when editing existing session
  useEffect(() => {
    if (initialData) {
      // Parse duration into hours and minutes
      const durationMinutes = initialData.durationMinutes || 0;
      const hours = Math.floor(durationMinutes / 60);
      const minutes = durationMinutes % 60;
      
      // Parse date
      let dateStr = new Date().toISOString().split('T')[0];
      if (initialData.date) {
        const d = new Date(initialData.date);
        dateStr = d.toISOString().split('T')[0];
      }
      
      // Map results from API format to form format
      const mappedResults = initialData.results?.map(r => ({
        playerId: String(r.playerId),
        rank: r.rank,
        score: r.rawScore ?? '', // raw_score from API
        leaguePoints: r.leaguePoints ?? r.points ?? null // Existing league points (read-only)
      })) || [{ playerId: '', rank: 1, score: '', leaguePoints: null }];
      
      reset({
        gameId: String(initialData.gameId),
        date: dateStr,
        hours,
        minutes,
        results: mappedResults
      });
      
      // If there's an existing session image, show it
      if (initialData.sessionImage) {
        setPreview(initialData.sessionImage);
      }
      
      // Set selected expansions if editing
      if (initialData.expansionIds) {
        setSelectedExpansionIds(initialData.expansionIds);
      }
    }
  }, [initialData, reset]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setPreview(URL.createObjectURL(selectedFile));
    }
  };

  const onSubmit = async (data) => {
    // 1. Calculate duration
    const durationMinutes = (parseInt(data.hours || 0) * 60) + parseInt(data.minutes || 0);

    // 2. Upload Image if exists (new file selected)
    let imageUrl = initialData?.sessionImage || null;
    if (file) {
      const formData = new FormData();
      formData.append('image', file);
      try {
        const res = await fetch('/api/upload', {
          method: 'POST',
          body: formData
        });
        if (!res.ok) throw new Error('Upload failed');
        const result = await res.json();
        imageUrl = result.url; // API returns 'url' not 'imageUrl'
      } catch (err) {
        console.error("Upload failed", err);
        alert("Kuvan lataus epäonnistui, tallennetaan ilman kuvaa.");
      }
    }

    // 3. Prepare payload - only send editable fields
    // Backend will recalculate leaguePoints based on rank
    const payload = {
        id: initialData?.id, // Include session ID if editing
        gameId: data.gameId,
        date: data.date,
        durationMinutes,
        imageUrl,
        results: data.results.map(r => ({
            playerId: r.playerId,
            rank: parseInt(r.rank),
            score: r.score ? parseInt(r.score) : null // Raw game score (Pisteet / VP)
            // Note: leaguePoints is NOT sent - backend calculates it
        })),
        expansionIds: selectedExpansionIds // Add used expansions
    };

    onSave(payload);
  };

  return (
    <div className="card">
      <h3>{isEditMode ? 'Muokkaa Sessiota' : 'Kirjaa Peli (Session)'}</h3>
      <form onSubmit={handleSubmit(onSubmit)}>
        
        <div className="form-group">
          <label>Peli</label>
          <select {...register("gameId", { required: true })}>
            <option value="">Valitse peli...</option>
            {games.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
          {errors.gameId && <span className="error">Pakollinen kenttä</span>}
        </div>

        <div className="form-group">
          <label>Päivämäärä</label>
          <input type="date" {...register("date", { required: true })} />
        </div>

        <div className="form-group">
            <label><Clock size={16} /> Kesto</label>
            <div style={{display: 'flex', gap: '10px', alignItems: 'center'}}>
                <div style={{flex: 1}}>
                    <input type="number" placeholder="Tunnit" {...register("hours")} min="0" />
                </div>
                <span>h</span>
                <div style={{flex: 1}}>
                    <input type="number" placeholder="Minuutit" {...register("minutes")} min="0" max="59" />
                </div>
                <span>min</span>
            </div>
        </div>

        <div className="form-group">
          <label>Tulokset</label>
          <small style={{display: 'block', marginBottom: '10px', color: '#666'}}>
            {isEditMode 
              ? 'Muokkaa sijoituksia ja pisteitä. Menestyspisteet lasketaan automaattisesti sijoituksen perusteella.'
              : 'Lisää pelaajat sijoitusjärjestyksessä.'
            }
          </small>
          
          {fields.map((item, index) => (
            <div key={item.id} className="result-row" style={{
                display: 'flex', 
                gap: '10px', 
                marginBottom: '10px', 
                background: '#f9f9f9', 
                padding: '10px', 
                borderRadius: '4px',
                alignItems: 'center',
                flexWrap: 'wrap'
            }}>
              <div style={{width: '60px'}}>
                <label style={{fontSize: '0.8rem'}}>Sija</label>
                <input 
                  type="number" 
                  {...register(`results.${index}.rank`, { required: true })} 
                />
              </div>
              
              <div style={{flex: 2, minWidth: '120px'}}>
                <label style={{fontSize: '0.8rem'}}>Pelaaja</label>
                <select {...register(`results.${index}.playerId`, { required: true })}>
                  <option value="">Valitse...</option>
                  {users.filter(u => u.isActive !== false).map(u => (
                      <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </select>
              </div>

              <div style={{width: '100px'}}>
                 <label style={{fontSize: '0.8rem'}}>Pisteet <span title="Pelin sisäiset pisteet" style={{color: '#999', cursor: 'help'}}>(VP)</span></label>
                 <input 
                    type="number" 
                    placeholder="esim. 145"
                    {...register(`results.${index}.score`)} 
                 />
              </div>

              {/* Display existing League Points as read-only badge (only in edit mode) */}
              {isEditMode && item.leaguePoints !== null && item.leaguePoints !== undefined && (
                <div style={{width: '80px', textAlign: 'center'}}>
                  <label style={{fontSize: '0.8rem', display: 'block'}}>MP</label>
                  <span 
                    className="league-points-badge" 
                    title="Menestyspisteet (lasketaan automaattisesti)"
                    style={{
                      display: 'inline-block',
                      background: '#d35400',
                      color: 'white',
                      padding: '6px 12px',
                      borderRadius: '12px',
                      fontWeight: 'bold',
                      fontSize: '0.9rem'
                    }}
                  >
                    <Award size={12} style={{verticalAlign: 'middle', marginRight: '4px'}} />
                    {item.leaguePoints}
                  </span>
                </div>
              )}

              <div style={{marginTop: '1.2rem'}}>
                <button type="button" className="btn-secondary" onClick={() => remove(index)} style={{padding: '0.5rem', background: '#e74c3c'}}>
                    <Trash2 size={16}/>
                </button>
              </div>
            </div>
          ))}
          
          <button 
            type="button" 
            className="btn-secondary" 
            onClick={() => append({ playerId: '', rank: fields.length + 1, score: '', leaguePoints: null })}
            style={{width: '100%', marginTop: '10px'}}
          >
            <Plus size={16} /> Lisää pelaaja
          </button>
        </div>

        {/* Expansions Selection */}
        {availableExpansions.length > 0 && (
          <div className="form-group">
            <label><Package size={16} /> Käytetyt lisäosat</label>
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '0.5rem',
              padding: '0.75rem',
              background: '#f5f5f5',
              borderRadius: '8px',
              border: '1px solid #ddd'
            }}>
              {loadingExpansions ? (
                <span style={{color: '#888', fontSize: '0.85rem'}}>Ladataan lisäosia...</span>
              ) : availableExpansions.map(exp => {
                const isSelected = selectedExpansionIds.includes(exp.id);
                return (
                  <button
                    key={exp.id}
                    type="button"
                    onClick={() => {
                      if (isSelected) {
                        setSelectedExpansionIds(prev => prev.filter(id => id !== exp.id));
                      } else {
                        setSelectedExpansionIds(prev => [...prev, exp.id]);
                      }
                    }}
                    style={{
                      padding: '0.5rem 1rem',
                      borderRadius: '20px',
                      border: isSelected ? '2px solid #22c55e' : '2px solid #ccc',
                      background: isSelected ? '#dcfce7' : 'white',
                      color: isSelected ? '#166534' : '#555',
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      fontWeight: isSelected ? '600' : '400',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.3rem'
                    }}
                  >
                    {isSelected && <span>✓</span>}
                    {exp.name}
                  </button>
                );
              })}
            </div>
            <small style={{color: '#666', marginTop: '0.25rem', display: 'block'}}>
              Valitse pelisessiossa käytetyt lisäosat
            </small>
          </div>
        )}

        <div className="form-group">
          <label>Kuva (Selfie / Lauta)</label>
          <div style={{border: '2px dashed #ccc', padding: '20px', textAlign: 'center', borderRadius: '8px'}}>
            <input 
              type="file" 
              accept="image/*" 
              id="file-upload" 
              style={{display: 'none'}} 
              onChange={handleFileChange}
            />
            <label htmlFor="file-upload" style={{cursor: 'pointer', display: 'inline-block'}}>
              <Upload size={32} />
              <p>{file ? file.name : (preview ? "Vaihda kuva" : "Ota kuva tai valitse tiedosto")}</p>
            </label>
          </div>
          {preview && <img src={preview} alt="Preview" className="file-upload-preview" style={{maxWidth: '200px', marginTop: '10px', borderRadius: '8px'}} />}
        </div>

        <div style={{display: 'flex', gap: '10px', marginTop: '20px'}}>
          <button type="button" className="btn-secondary" onClick={onCancel}>Peruuta</button>
          <button type="submit" className="btn-primary">
            <Save size={18} /> {isEditMode ? 'Tallenna Muutokset' : 'Tallenna Session'}
          </button>
        </div>

      </form>
    </div>
  );
};

export default SessionForm;
